import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from './ui/button'
import { Trash2 } from 'lucide-react'
import { useCart } from '../context/CartContext'
import { buildProductAlt } from '../lib/altText'
import { ImageWithFallback } from './figma/ImageWithFallback'
import { buildResponsiveImageForWidth } from '../lib/artwork'
import { trackAnalyticsEvent } from '../lib/analytics'
import { useTranslation } from '../context/LanguageContext'
import { useCurrency } from '../context/CurrencyContext'
import { formatPriceForCurrency } from '../lib/currency'

interface CartPanelProps {
  open: boolean
  onClose: () => void
}

const buildCartThumbnail = (src?: string | null) => {
  const image = buildResponsiveImageForWidth(src, { targetWidth: 360 })
  return {
    src: image.src,
    srcSet: image.srcSet,
    sizes: '(max-width: 768px) 64px, 96px',
  }
}

export function CartPanel({ open, onClose }: CartPanelProps) {
  const { items, subtotal, totalQuantity, removeItem, clearCart } = useCart()
  const navigate = useNavigate()
  const prevItemCountRef = useRef(items.length)
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const formatCartPrice = (value: number) => formatPriceForCurrency(value, currency)
  useEffect(() => {
    if (!open) {
      prevItemCountRef.current = items.length
      return
    }
    if (prevItemCountRef.current > 0 && items.length === 0) {
      onClose()
    }
    prevItemCountRef.current = items.length
  }, [items.length, open, onClose])
  const goToCheckout = () => {
    onClose()
    trackAnalyticsEvent('checkout_start_clicked', {
      metadata: { itemCount: items.length, subtotal: subtotal.toFixed(2) },
    })
    navigate('/checkout')
  }
  if (!open) return null

  return (
    <div className="cart-overlay" role="dialog" aria-modal="true">
      <div className="cart-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="cart-panel">
        <header className="cart-panel__header">
          <div>
            <p className="cart-panel__eyebrow">{t('cart.title', 'Your cart')}</p>
            <p className="cart-panel__title">
              {totalQuantity}{' '}
              {totalQuantity === 1 ? t('cart.item.singular', 'item') : t('cart.item.plural', 'items')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="cart-panel__close"
            aria-label="Close cart"
          >
            ×
          </button>
        </header>
        <div className="cart-panel__body">
          {items.length === 0 && (
            <p className="text-sm text-foreground/60">
              {t('cart.empty', 'No items yet. Add a product to see it here.')}
            </p>
          )}
          {items.map((item) => {
            const thumbnail = buildCartThumbnail(item.image)
            return (
            <div key={item.id} className="cart-panel__item">
              <div className="cart-panel__thumb">
                <ImageWithFallback
                  src={thumbnail.src}
                  srcSet={thumbnail.srcSet}
                  sizes={thumbnail.sizes}
                  loading="lazy"
                  alt={buildProductAlt(item.productTitle)}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="cart-panel__item-details">
                <p className="text-base font-medium">{item.productTitle}</p>
                <p className="text-sm text-foreground/60">{item.sizeLabel}</p>
                <p className="text-sm">
                  {item.quantity} × {formatCartPrice(item.unitPrice)}
                </p>
                <p className="text-base font-semibold">
                  {formatCartPrice(item.quantity * item.unitPrice)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="cart-panel__remove"
                aria-label={t('cart.removeItem', `Remove ${item.productTitle} from cart`)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )})}
        </div>
        <footer className="cart-panel__footer">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground/60">{t('cart.subtotal', 'Subtotal')}</span>
            <span className="text-lg font-semibold">{formatCartPrice(subtotal)}</span>
          </div>
          <Button className="w-full" size="lg" disabled={items.length === 0} onClick={goToCheckout}>
            {t('cart.checkout', 'Checkout')}
          </Button>
          <Button
            variant="ghost"
            className={`w-full${items.length === 0 ? ' opacity-60' : ''}`}
            onClick={() => {
              clearCart()
              onClose()
            }}
            disabled={items.length === 0}
          >
            {t('cart.clear', 'Clear cart')}
          </Button>
        </footer>
      </aside>
    </div>
  )
}
